// Web mock for react-native-reanimated
import React from 'react';
import { View, Text, ScrollView, Image } from 'react-native';

const CSS_TRANSITION = 'height 300ms cubic-bezier(0.4,0,0.2,1), opacity 300ms cubic-bezier(0.4,0,0.2,1), transform 300ms cubic-bezier(0.4,0,0.2,1), background-color 300ms cubic-bezier(0.4,0,0.2,1)';

function useTransitionRef(outerRef) {
  const innerRef = React.useRef(null);
  React.useEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    // Wait two frames so the initial layout/measurement paint completes
    // before enabling CSS transitions — prevents unwanted mount animations.
    let raf1 = requestAnimationFrame(() => {
      let raf2 = requestAnimationFrame(() => {
        if (node && node.style) node.style.transition = CSS_TRANSITION;
      });
      raf1 = raf2;
    });
    return () => cancelAnimationFrame(raf1);
  }, []);
  const setRef = React.useCallback((node) => {
    innerRef.current = node;
    if (!outerRef) return;
    if (typeof outerRef === 'function') outerRef(node);
    else outerRef.current = node;
  }, [outerRef]);
  return setRef;
}

// Mock Animated API — delegates to react-native-web components for proper
// RN style conversion (transform arrays, etc.) and injects CSS transition
// so style changes animate smoothly in the browser.
export const Animated = {
  View: React.forwardRef((props, ref) => {
    const combinedRef = useTransitionRef(ref);
    return React.createElement(View, { ...props, ref: combinedRef });
  }),
  Text: React.forwardRef((props, ref) => React.createElement(Text, { ...props, ref })),
  ScrollView: React.forwardRef((props, ref) => React.createElement(ScrollView, { ...props, ref })),
  Image: React.forwardRef((props, ref) => React.createElement(Image, { ...props, ref })),
};

// Mock hooks - use mutable ref to match reanimated's shared value semantics
export function useSharedValue(initialValue) {
  const ref = React.useRef({ value: initialValue });
  // Re-render trigger for useAnimatedStyle consumers
  const [, forceRender] = React.useState(0);
  const proxy = React.useMemo(() => {
    return {
      get value() {
        return ref.current.value;
      },
      set value(newValue) {
        ref.current.value = newValue;
        forceRender((n) => n + 1);
      },
    };
  }, []);
  return proxy;
}

export function useAnimatedStyle(styleFunction) {
  try {
    return styleFunction();
  } catch {
    return {};
  }
}

export function useAnimatedGestureHandler(handlers) {
  return handlers;
}

export function useDerivedValue(derivedFunction) {
  return React.useMemo(() => {
    try {
      return derivedFunction();
    } catch {
      return null;
    }
  }, [derivedFunction]);
}

export function useAnimatedReaction(prepare, react) {
  React.useEffect(() => {
    try {
      const prepared = prepare();
      react(prepared);
    } catch {
      // Ignore errors in web mock
    }
  }, [prepare, react]);
}

// Extrapolation enum for interpolate calls
export const Extrapolation = {
  CLAMP: 'clamp',
  EXTEND: 'extend',
  IDENTITY: 'identity',
};

// ReduceMotion enum — mirrors react-native-reanimated. Passed as the
// `reduceMotion` argument to withTiming/withRepeat (the passthrough animations
// below ignore it, but it must exist so `ReduceMotion.Never` resolves on web).
export const ReduceMotion = {
  System: 'system',
  Always: 'always',
  Never: 'never',
};

// useReducedMotion — reflects the OS "reduce motion" preference. On web that is
// the `prefers-reduced-motion` media query; reactive so consumers re-render if
// the user toggles the setting. Mirrors react-native-reanimated's hook.
export function useReducedMotion() {
  const QUERY = '(prefers-reduced-motion: reduce)';
  const supported =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [reduced, setReduced] = React.useState(() =>
    supported ? window.matchMedia(QUERY).matches : false,
  );
  React.useEffect(() => {
    if (!supported) return undefined;
    const mql = window.matchMedia(QUERY);
    const onChange = () => setReduced(mql.matches);
    onChange();
    // Safari < 14 only supports the deprecated addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [supported]);
  return reduced;
}

// Mock animation functions — withTiming/withSpring accept an optional
// callback as a 3rd argument. On web the value resolves immediately,
// so we call the callback synchronously with finished=true.
export function withTiming(value, config = {}, callback) {
  if (typeof callback === 'function') callback(true);
  return value;
}

export function withSpring(value, config = {}, callback) {
  if (typeof callback === 'function') callback(true);
  return value;
}

export function withDecay(config = {}) {
  return config.velocity || 0;
}

export function withDelay(delay, animation) {
  return animation;
}

export function withRepeat(animation, numberOfReps = -1, reverse = false) {
  return animation;
}

export function withSequence(...animations) {
  return animations[0] || 0;
}

export function cancelAnimation(sharedValue) {
  // No-op for web
}

export function runOnJS(fn) {
  return fn;
}

export function runOnUI(fn) {
  return fn;
}

// Provide createAnimatedComponent used by RNGH and others
export function createAnimatedComponent(Component) {
  return Component;
}

// useEvent is called internally by react-native-gesture-handler's useAnimatedGesture.
// Return a stub event handler object so gesture detection doesn't crash on web.
export function useEvent(callback, eventNames, rebuild) {
  return { current: null };
}

// Mock interpolation
export function interpolate(value, inputRange, outputRange, extrapolate = 'clamp') {
  if (inputRange.length !== outputRange.length) {
    return outputRange[0];
  }
  
  // Simple linear interpolation for web
  for (let i = 0; i < inputRange.length - 1; i++) {
    if (value >= inputRange[i] && value <= inputRange[i + 1]) {
      const progress = (value - inputRange[i]) / (inputRange[i + 1] - inputRange[i]);
      return outputRange[i] + progress * (outputRange[i + 1] - outputRange[i]);
    }
  }
  
  return extrapolate === 'clamp' 
    ? (value < inputRange[0] ? outputRange[0] : outputRange[outputRange.length - 1])
    : value;
}

export function interpolateColor(value, inputRange, outputRange) {
  // Simple color interpolation fallback
  return outputRange[0] || '#000000';
}

// Mock Easing
export const Easing = {
  linear: (t) => t,
  ease: (t) => t,
  quad: (t) => t * t,
  cubic: (t) => t * t * t,
  bezier: () => (t) => t,
  in: (easing) => easing,
  out: (easing) => easing,
  inOut: (easing) => easing,
};

// Default export matches react-native-reanimated's default export shape:
// `import Animated from 'react-native-reanimated'` then `Animated.View`, etc.
export default {
  ...Animated,
  createAnimatedComponent,
  useSharedValue,
  useEvent,
};
